import React, { useCallback, useMemo, useRef, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import Card from "../../ui/Card";
import { useTheme } from "../../../contexts/ThemeContext";
import { hexToRgba } from "../../../utils/themes";

const CHART_SLICE_COUNT = 25;
const INITIAL_LABEL_COUNT = 5;
const LABEL_INCREMENT = 10;
const OTHER_EMAIL_TYPES_LABEL = "All other email types";

function opacityForSlice(name, hoveredName, selectedNames) {
  if (hoveredName && hoveredName !== name) return 0.28;
  if (hoveredName === name) return 1;
  if (selectedNames.size > 0 && !selectedNames.has(name)) return 0.14;
  return 1;
}

function normalizeEmailTypeLabel(value) {
  const cleaned = (value || "")
    .replace(/^other:\s*/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "Unknown";
  if (cleaned.toLowerCase() === "cold pitch") return "Cold Email";
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
}

function pluralizeEmailType(label, count) {
  if (count === 1) return label;
  if (/email$/i.test(label)) return `${label}s`;
  if (/emails$/i.test(label)) return label;
  return label;
}

function EmailTypeTooltip({ payload, totalEmails, theme }) {
  const share = totalEmails ? Math.round((1000 * payload.count) / totalEmails) / 10 : 0;
  return (
    <div className="min-w-[260px] max-w-[320px] space-y-3">
      <div>
        <div className="font-semibold text-theme-on-background text-[14px] leading-tight">{payload.name}</div>
        <div className="mt-1 font-mono text-[10px] text-theme-muted leading-snug">
          {payload.count} processed emails, {share}% of this mailbox sample
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded border border-theme-border bg-theme-secondary px-2 py-1.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.08em] text-theme-muted">Emails</div>
          <div className="text-sm font-semibold text-theme-on-background tabular-nums">{payload.count}</div>
        </div>
        <div className="rounded border border-theme-border bg-theme-secondary px-2 py-1.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.08em] text-theme-muted">Cold</div>
          <div className="text-sm font-semibold text-theme-on-background tabular-nums">{payload.coldPitchCount}</div>
        </div>
        <div className="rounded border border-theme-border bg-theme-secondary px-2 py-1.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.08em] text-theme-muted">Matches</div>
          <div className="text-sm font-semibold text-theme-on-background tabular-nums">{payload.matchCount}</div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="space-y-1">
          <div className="flex items-center justify-between font-mono text-[10px] text-theme-muted">
            <span>Cold inbound rate</span>
            <span className="tabular-nums">{payload.pitchRatePct}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: hexToRgba(theme.border, 0.55) }}>
            <div className="h-full rounded-full" style={{ width: `${payload.pitchRatePct}%`, background: theme.accent }} />
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between font-mono text-[10px] text-theme-muted">
            <span>ICP match rate</span>
            <span className="tabular-nums">{payload.matchRatePct}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: hexToRgba(theme.border, 0.55) }}>
            <div
              className="h-full rounded-full"
              style={{ width: `${payload.matchRatePct}%`, background: hexToRgba(theme.accent, 0.62) }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Volume mix by email type; opacity scales with match rate among cold pitches in that type. */
export default function AuditEmailTypeDonut({ rows }) {
  const { theme } = useTheme();
  const [hoveredName, setHoveredName] = useState(null);
  const [selectedNames, setSelectedNames] = useState(() => new Set());
  const [visibleLabelCount, setVisibleLabelCount] = useState(INITIAL_LABEL_COUNT);
  const chartAreaRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState(null);

  const updateTooltipPos = useCallback((event) => {
    const rect = chartAreaRef.current?.getBoundingClientRect();
    if (!rect) return;
    const offset = 14;
    setTooltipPos({
      x: event.clientX - rect.left + offset,
      y: event.clientY - rect.top + offset,
    });
  }, []);

  const clearTooltipPos = useCallback(() => setTooltipPos(null), []);

  const toggleName = useCallback((name) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const data = useMemo(() => {
    const r = rows || [];
    const buckets = new Map();
    for (const row of r) {
      const name = normalizeEmailTypeLabel(row.segment);
      const prev = buckets.get(name) || { name, count: 0, coldPitchCount: 0, matchCount: 0 };
      const count = row.count ?? 0;
      const coldPitchCount = row.cold_pitch_count ?? Math.round((count * Number(row.pitch_rate_pct ?? 0)) / 100);
      const matchCount = Math.round((coldPitchCount * Number(row.match_rate_pct ?? 0)) / 100);
      buckets.set(name, {
        name,
        count: prev.count + count,
        coldPitchCount: prev.coldPitchCount + coldPitchCount,
        matchCount: prev.matchCount + matchCount,
      });
    }
    const mapped = [...buckets.values()]
      .map((row) => ({
        ...row,
        pitchRatePct: row.count ? Math.round((1000 * row.coldPitchCount) / row.count) / 10 : 0,
        matchRatePct: row.coldPitchCount ? Math.round((1000 * row.matchCount) / row.coldPitchCount) / 10 : 0,
      }))
      .filter((d) => d.count > 0)
      .sort((a, b) => b.count - a.count);
    const maxMatch = Math.max(0, ...mapped.map((d) => d.matchRatePct));
    return mapped.map((d) => ({
      ...d,
      fillOpacity: maxMatch > 0 ? Math.max(0.12, d.matchRatePct / maxMatch) : 1,
    }));
  }, [rows]);

  const totalEmails = data.reduce((sum, d) => sum + d.count, 0);
  const chartData = useMemo(() => {
    if (data.length <= CHART_SLICE_COUNT) return data;
    const visible = data.slice(0, CHART_SLICE_COUNT);
    const tail = data.slice(CHART_SLICE_COUNT).reduce(
      (acc, row) => ({
        ...acc,
        count: acc.count + row.count,
        coldPitchCount: acc.coldPitchCount + row.coldPitchCount,
        matchCount: acc.matchCount + row.matchCount,
      }),
      {
        name: OTHER_EMAIL_TYPES_LABEL,
        count: 0,
        coldPitchCount: 0,
        matchCount: 0,
        fillOpacity: 0.16,
      }
    );
    tail.pitchRatePct = tail.count ? Math.round((1000 * tail.coldPitchCount) / tail.count) / 10 : 0;
    tail.matchRatePct = tail.coldPitchCount ? Math.round((1000 * tail.matchCount) / tail.coldPitchCount) / 10 : 0;
    return [...visible, tail];
  }, [data]);
  const centerCount =
    selectedNames.size > 0
      ? chartData.filter((d) => selectedNames.has(d.name)).reduce((sum, d) => sum + d.count, 0)
      : totalEmails;
  const visibleData = data.slice(0, visibleLabelCount);
  const hiddenLabelCount = Math.max(0, data.length - visibleData.length);

  if (!data.length) {
    return (
      <Card className="p-4 md:p-5 h-full">
        <h3 className="font-display font-bold italic text-base text-theme-on-background mb-1">Incoming email types</h3>
        <p className="text-theme-muted text-sm">No category breakdown for this run.</p>
      </Card>
    );
  }

  return (
    <Card className="p-4 md:p-5 h-full flex flex-col">
      <h3 className="font-display font-bold italic text-lg text-theme-on-background mb-1">Incoming email types</h3>
      <p className="font-mono text-[10px] text-theme-dim mb-2 leading-snug">
        All processed mailbox categories. Hover a chart section for cold inbound and match rates.
      </p>
      <div
        ref={chartAreaRef}
        className="relative h-[200px] w-full flex-1 min-h-[160px] overflow-visible"
        onMouseMove={updateTooltipPos}
        onMouseLeave={clearTooltipPos}
      >
        <ResponsiveContainer width="100%" height="100%" style={{ overflow: "visible" }}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="count"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={44}
              outerRadius={72}
              paddingAngle={2}
            >
              {chartData.map((d, i) => {
                const fill = hexToRgba(theme.accent, 0.18 + 0.82 * d.fillOpacity);
                const mult = opacityForSlice(d.name, hoveredName, selectedNames);
                return (
                  <Cell
                    key={`c-${i}`}
                    fill={fill}
                    fillOpacity={mult}
                    stroke={theme.border}
                    style={{ transition: "fill-opacity 0.18s ease" }}
                  />
                );
              })}
            </Pie>
            <Tooltip
              cursor={false}
              offset={0}
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ pointerEvents: "none", zIndex: 30, outline: "none", overflow: "visible" }}
              contentStyle={{ background: "transparent", border: "none", padding: 0, boxShadow: "none" }}
              position={tooltipPos ?? undefined}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div
                    className="rounded-lg border border-theme-border px-3 py-2 text-xs shadow-lg"
                    style={{ background: theme.secondary, color: theme.text }}
                  >
                    <EmailTypeTooltip payload={p} totalEmails={totalEmails} theme={theme} />
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-2">
          <div className="flex flex-col items-center text-center">
            <div className="text-base md:text-lg font-bold text-theme-on-background tabular-nums leading-none">
              {centerCount}
            </div>
            <div className="mt-0.5 text-[11px] md:text-xs font-medium text-theme-text opacity-55 leading-none">
              {centerCount === 1 ? "email" : "emails"}
            </div>
          </div>
        </div>
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-2 font-mono text-[10px] text-theme-text w-full">
        {visibleData.map((d, i) => {
          const sel = selectedNames.has(d.name);
          const dotFill = hexToRgba(theme.accent, 0.18 + 0.82 * d.fillOpacity);
          return (
            <li key={`${d.name}-${i}`} className="max-w-full">
              <button
                type="button"
                className={`flex items-start gap-2 min-w-0 text-left rounded px-1 -mx-1 py-0.5 cursor-pointer border border-transparent transition-opacity duration-150 ${
                  sel ? "border-theme-secondary" : ""
                }`}
                style={{ opacity: opacityForSlice(d.name, hoveredName, selectedNames) }}
                aria-pressed={sel}
                onMouseEnter={() => setHoveredName(d.name)}
                onMouseLeave={() => setHoveredName(null)}
                onClick={() => toggleName(d.name)}
              >
                <span
                  className="shrink-0 w-2 h-2 rounded-full mt-0.5 pointer-events-none"
                  style={{ background: dotFill }}
                />
                <span className="min-w-0 flex-1 leading-snug whitespace-normal break-words pointer-events-none">
                  <span className="text-theme-on-background">
                    {d.count} {pluralizeEmailType(d.name, d.count)}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {hiddenLabelCount > 0 || visibleLabelCount > INITIAL_LABEL_COUNT ? (
        <button
          type="button"
          className="mt-2 self-start font-mono text-[10px] uppercase tracking-[0.1em] text-theme-primary hover:opacity-80"
          onClick={() => {
            if (hiddenLabelCount > 0) {
              setVisibleLabelCount((prev) => Math.min(data.length, prev + LABEL_INCREMENT));
            } else {
              setVisibleLabelCount(INITIAL_LABEL_COUNT);
            }
          }}
        >
          {hiddenLabelCount > 0 ? `View more (${Math.min(LABEL_INCREMENT, hiddenLabelCount)})` : "View less"}
        </button>
      ) : null}
    </Card>
  );
}
